import { ReactElement } from 'react';
import FormGroup from '~/components/form/FormGroup';
import { FormHorizontal } from '~/components/form/layout';
import { useTz } from '~/components/hooks/useTz';
import { InputCardOption } from '~/components/input/InputCards';
import ZodFieldDate from '~/components/zod/ZodFieldDate';
import ZodFieldCards from '~/components/zod/ZodFieldCards';
import { useZodFormFieldSingleValue } from '~/components/zod/ZodForm';
import {
  BusinessCardCount,
  BusinessCardPaper,
  countSchema,
  paperSchema,
} from '~/tenants/bre/performable/print/cards/BreBusinessCardsConfig';
import { BreBusinessCardsRate } from '~/tenants/bre/performable/print/cards/BreBusinessCardsData';
import BreBusinessCardsJob from '~/tenants/bre/performable/print/cards/BreBusinessCardsJob';
import { isBusinessDay, plusBusinessDays } from '~/tenants/bre/performable/common';

export default function BreBusinessCardsForm(): ReactElement {
  const tz = useTz();
  const count = useZodFormFieldSingleValue<BusinessCardCount>('count') ?? '50';
  const paper = useZodFormFieldSingleValue<BusinessCardPaper>('paper') ?? 'uv_glossy';

  const paperOptions = paperSchema.options.map<InputCardOption>((value) => ({
    title: BreBusinessCardsJob.paperType(value),
    description: '16 pt. Cover stock',
    disabled: !BreBusinessCardsRate[value][count],
    value,
  }));

  const countOptions = countSchema.options.map<InputCardOption>((value) => {
    const rate = BreBusinessCardsRate[paper][value];

    return {
      title: `${value} cards`,
      cost: rate,
      disabled: !rate,
      value,
    };
  });

  return (
    <FormGroup>
      <FormHorizontal name="paper" label="Paper" required>
        <ZodFieldCards name="paper" options={paperOptions} cols={2} required />
      </FormHorizontal>
      <FormHorizontal name="count" label="Count" required>
        <ZodFieldCards name="count" options={countOptions} required />
      </FormHorizontal>
      <FormHorizontal name="need_by" label="Need By" required>
        <ZodFieldDate
          name="need_by"
          filter={(date) => isBusinessDay(date) && date > plusBusinessDays(tz().startOf('day'), 2)}
        />
      </FormHorizontal>
    </FormGroup>
  );
}
